import { useState } from 'react'
import { useTranslations } from 'use-intl'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import { Label } from '@/components/ui/label'
import { ServiceRegistry } from '@/services/ServiceRegistry'
import { useCompany } from '@/contexts/CompanyContext'
import type { TargetSystem } from '@/clients/documentExportApiClient'

type Props = {
  open: boolean
  onOpenChange: (open: boolean) => void
  fiscalYearId: string | undefined
  fiscalYearLabel: string
}

export function DocumentExportDialog({ open, onOpenChange, fiscalYearId, fiscalYearLabel }: Props) {
  const t = useTranslations('consultantBooks.journal.export')
  const { company } = useCompany()
  const [targetSystem, setTargetSystem] = useState<TargetSystem>('bokio')
  const [loading, setLoading] = useState(false)

  async function handleDownload() {
    if (!fiscalYearId || !company) return
    const slug = company.name
      .toLowerCase()
      .replace(/[^a-z0-9åäöÅÄÖ]+/g, '-')
      .replace(/^-+|-+$/g, '')
    const filename = `${slug}_${fiscalYearLabel}_${targetSystem}_export.zip`
    setLoading(true)
    try {
      await ServiceRegistry.getDocumentExportService().exportDocuments(fiscalYearId, targetSystem, filename)
    } finally {
      setLoading(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>{t('title')}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <p className="text-sm text-muted-foreground">
            {t('description', { year: fiscalYearLabel })}
          </p>

          <RadioGroup
            value={targetSystem}
            onValueChange={(v) => setTargetSystem(v as TargetSystem)}
            className="space-y-2"
          >
            <div className="flex items-center gap-2">
              <RadioGroupItem value="bokio" id="bokio" />
              <Label htmlFor="bokio">{t('bokio')}</Label>
            </div>
            <div className="flex items-center gap-2">
              <RadioGroupItem value="fortnox" id="fortnox" />
              <Label htmlFor="fortnox">{t('fortnox')}</Label>
            </div>
          </RadioGroup>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={loading}>
            {t('cancel')}
          </Button>
          <Button onClick={handleDownload} disabled={loading || !fiscalYearId || !company}>
            {loading ? t('downloading') : t('download')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
